import { getTableColumns } from "drizzle-orm";
import {
  index,
  pgEnum,
  pgTable,
  text,
  unique,
  uuid,
} from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import z from "zod";

import {
  householdMemberSchema,
  householdMembersTable,
} from "./householdMembersSchema";
import { customTimestamp } from "./utils/types";

export const relationshipEnumSchema = z.enum([
  "SPOUSE",
  "PARENT",
  "GRANDPARENT",
  "SIBLING",
  "PARTNER",
  "CHILD",
  "STEPCHILD",
  "GRANDCHILD",
  "FOSTER_CHILD",
  "OTHER",
]);
export type RelationshipEnumType = z.infer<typeof relationshipEnumSchema>;
export const relationshipEnum = pgEnum(
  "relationship_enum",
  relationshipEnumSchema.enum,
);

export const livingWithStatusEnumSchema = z.enum([
  "LIVING_WITH",
  "NOT_LIVING_WITH",
]);
export type LivingWithStatusEnumType = z.infer<
  typeof livingWithStatusEnumSchema
>;
export const livingWithStatusEnum = pgEnum(
  "living_with_status_enum",
  livingWithStatusEnumSchema.enum,
);

export const taxRelationshipEnumSchema = z.enum([
  "SUPPORT_OF",
  "FILES_JOINTLY_WITH",
  "DEPENDENT_OF",
  "DEPENDENT_OF_SPOUSE",
  "DEPENDENT_OF_SUPPORTER",
]);
export type TaxRelationshipEnumType = z.infer<typeof taxRelationshipEnumSchema>;
export const taxRelationshipEnum = pgEnum(
  "tax_relationship_enum",
  taxRelationshipEnumSchema.enum,
);

export const householdMemberRelationshipsTable = pgTable(
  "household_member_relationships",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    primaryHouseholdMemberId: uuid("primary_household_member_id")
      .references(() => householdMembersTable.id, {
        onDelete: "cascade",
      })
      .notNull(),
    relationshipHouseholdMemberId: uuid("relationship_household_member_id")
      .references(() => householdMembersTable.id, {
        onDelete: "cascade",
      })
      .notNull(),
    relationship: relationshipEnum("relationship").notNull(),
    otherRelationship: text("other_relationship"),
    livingWithStatus: livingWithStatusEnum("living_with_status").notNull(),
    taxRelationship: taxRelationshipEnum("tax_relationship"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  table => [
    index(
      "household_member_relationships_primary_household_member_id_index",
    ).on(table.primaryHouseholdMemberId),
    unique("household_member_relationships_unique").on(
      table.primaryHouseholdMemberId,
      table.relationshipHouseholdMemberId,
    ),
  ],
);

export const householdMemberRelationshipsInsertSchema = createInsertSchema(
  householdMemberRelationshipsTable,
  {
    relationship: relationshipEnumSchema,
    livingWithStatus: livingWithStatusEnumSchema,
    taxRelationship: taxRelationshipEnumSchema.nullish(),
  },
).omit({ createdAt: true, updatedAt: true });
export type HouseholdMemberRelationshipsInsertType = z.infer<
  typeof householdMemberRelationshipsInsertSchema
>;

export const householdMemberRelationshipsSchema = createSelectSchema(
  householdMemberRelationshipsTable,
  {
    taxRelationship: taxRelationshipEnumSchema.nullish(),
    relationship: relationshipEnumSchema,
    livingWithStatus: livingWithStatusEnumSchema,
  },
);
export type HouseholdMemberRelationshipsType = z.infer<
  typeof householdMemberRelationshipsSchema
>;

export const householdMemberRelationshipsUpdateSchema =
  householdMemberRelationshipsInsertSchema
    .omit({
      id: true,
      primaryHouseholdMemberId: true,
      relationshipHouseholdMemberId: true,
    })
    .partial();
export type HouseholdMemberRelationshipsUpdateType = z.infer<
  typeof householdMemberRelationshipsUpdateSchema
>;

export const householdMemberRelationshipsTableColumns = getTableColumns(
  householdMemberRelationshipsTable,
);

export const householdMemberRelationshipWithMemberSchema =
  householdMemberRelationshipsSchema.extend({
    relationshipMember: householdMemberSchema,
  });
